import { useState } from 'react';
import { Alert, Badge, Button, Card, DateField, Field, Switch, Table } from '@evogard/ui';
import type { HolidayRecord } from '@evogard/contract';
import { useSessionContext } from '../../../lib/session.js';
import {
  useDeadlinePermissions,
  useHolidayMutations,
  useHolidays,
} from '../claim/useDeadlines.js';

const DAY = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
});

/** `AAAA-MM-DD` como a casa lê: "qui., 25/12/2026". O recorrente, sem o ano. */
function dayText(h: HolidayRecord): string {
  const [y, m, d] = h.date.split('-');
  if (h.recurring) return `${d}/${m}, todo ano`;
  return DAY.format(new Date(`${y}-${m}-${d}T12:00:00Z`));
}

/**
 * Os feriados da praça, na tela de SLA das fases (15/10/2026).
 *
 * A lista é da PLATAFORMA (`app.holidays`, `/platform/holidays`): todo
 * departamento que conta dias úteis lê a mesma. Ela mora aqui por ora porque
 * o sinistro é o primeiro a contar com ela — no SLA em dias úteis e no
 * "Vencimento do último contato".
 *
 * Mudar a lista não recalcula prazo já gravado: vale para o que for contado
 * depois. A tela diz isso.
 */
export function HolidaysPanel() {
  const { data: context } = useSessionContext();
  const { canEditHolidays } = useDeadlinePermissions();
  const holidays = useHolidays();
  const { create, remove } = useHolidayMutations();
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [tried, setTried] = useState(false);

  const missing = { date: date === '', name: name.trim() === '' };
  const submit = () => {
    setTried(true);
    if (missing.date || missing.name) return;
    create.mutate(
      { date, name: name.trim(), recurring },
      {
        onSuccess: () => {
          setDate('');
          setName('');
          setRecurring(false);
          setTried(false);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {canEditHolidays && (
        <Card
          title="Cadastrar feriado"
          descricao={`Vale para ${context?.currentTenant.nome ?? 'esta base'}. Feriado nacional é cadastrado em cada base.`}
        >
          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-start"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div className="sm:w-44">
              <DateField
                label="Data"
                required
                value={date}
                onChange={(v) => {
                  create.reset();
                  setDate(v ?? '');
                }}
                error={tried && missing.date ? 'Obrigatório.' : null}
              />
            </div>
            <div className="min-w-0 flex-1">
              <Field
                label="Nome"
                required
                maxLength={80}
                placeholder="Natal, Carnaval, Data Magna do Ceará"
                value={name}
                onChange={(e) => {
                  create.reset();
                  setName(e.target.value);
                }}
                error={tried && missing.name ? 'Obrigatório.' : null}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-texto">Repete</span>
              <Switch
                label="Repete todo ano"
                checked={recurring}
                onCheckedChange={setRecurring}
                onLabel="Todo ano"
                offLabel="Só neste ano"
              />
            </div>
            <div className="sm:pt-5.5">
              <Button type="submit" loading={create.isPending}>
                Cadastrar
              </Button>
            </div>
          </form>
          {create.isError && (
            <Alert tone="erro" className="mt-3">
              {create.error.message}
            </Alert>
          )}
        </Card>
      )}

      <Card
        title={`Feriados · ${holidays.data?.length ?? 0}`}
        descricao="As etapas em dias úteis e o vencimento do último contato pulam estes dias e o fim de semana. Mudar a lista vale para o que for contado daqui em diante; prazo já gravado não muda."
        bleed
      >
        {holidays.isError ? (
          <Alert tone="erro">{holidays.error.message}</Alert>
        ) : (
          <Table
            paginate={false}
            label="Feriados da base"
            items={holidays.data ?? []}
            rowKey={(h) => h.id}
            rowTitle={(h) => h.name}
            empty={
              holidays.isLoading
                ? 'Carregando os feriados…'
                : 'Nenhum feriado cadastrado. Em dias úteis, só o fim de semana é pulado.'
            }
            columns={[
              {
                title: 'Data',
                alignment: 'inicio' as const,
                minWidth: '10rem',
                cell: (h) => <span className="tabular-nums">{dayText(h)}</span>,
              },
              {
                title: 'Nome',
                alignment: 'inicio' as const,
                minWidth: '12rem',
                cell: (h) => <span className="font-medium">{h.name}</span>,
              },
              {
                title: 'Repete',
                alignment: 'inicio' as const,
                minWidth: '7rem',
                cell: (h) =>
                  h.recurring ? (
                    <Badge tone="informacao">todo ano</Badge>
                  ) : (
                    <span className="text-texto-tenue">só neste ano</span>
                  ),
              },
              ...(canEditHolidays
                ? [
                    {
                      title: 'Ações',
                      minWidth: '6rem',
                      cell: (h: HolidayRecord) => (
                        <Button
                          size="sm"
                          variant="discreto"
                          loading={remove.isPending && remove.variables === h.id}
                          disabled={remove.isPending}
                          aria-label={`Remover ${h.name}`}
                          onClick={() => remove.mutate(h.id)}
                        >
                          Remover
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}
        {remove.isError && <Alert tone="erro">{remove.error.message}</Alert>}
      </Card>
    </div>
  );
}
